import { readCookie } from './utils';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function fetchJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  const method = init.method || 'GET';
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('csrf');
    if (csrf) headers.set('X-CSRF-Token', csrf);
    if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
  }
  const res = await fetch(url, { ...init, headers, credentials: 'same-origin' });
  if (res.status === 204) return undefined as unknown as T;
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg =
      typeof data === 'object' && data && 'error' in data
        ? String((data as { error: unknown }).error)
        : res.statusText;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export async function uploadFile<T>(url: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  return fetchJson<T>(url, { method: 'POST', body: fd });
}

// ---------------- shared response shapes ----------------

export interface SessionUser {
  email: string;
  name: string;
  picture: string | null;
  isSuperAdmin: boolean;
}

export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
}

export type OrgPlan = 'trial' | 'active';

export interface ActiveOrg extends OrgSummary {
  plan: OrgPlan;
  trialEndsAt: string | null;
  trialExpired: boolean;
  trialMsRemaining: number;
}

export interface AdminOrg extends OrgSummary {
  createdAt: string;
  createdBy: string | null;
  plan: OrgPlan;
  trialEndsAt: string | null;
  trialExpired: boolean;
  adminCount: number;
  customerCount: number;
  recordsThisMonth: number;
}

export interface Customer {
  id: string;
  orgId: string;
  phone: string;
  name: string | null;
  createdVia: 'manual' | 'import' | 'ivr';
  createdAt: string;
  updatedAt: string;
}

export type AfterAction = 'hangup' | 'main' | 'extension';
export type QuestionnaireAfterAction = 'inherit' | AfterAction;

export interface SheetStatus {
  connected: boolean;
  ownerEmail: string | null;
  enabled: boolean;
  sheetId: string | null;
  url: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
}

export type SheetAccessReason =
  | 'no_sheet'
  | 'not_connected'
  | 'trashed'
  | 'missing'
  | 'check_error';

export interface SheetAccess {
  accessible: boolean;
  reason?: SheetAccessReason;
}

export interface GoogleConnectionStatus {
  connected: boolean;
  ownerEmail: string | null;
  redirectUri: string;
}

export interface Questionnaire {
  id: string;
  orgId: string;
  name: string;
  ivrName: string;
  description: string | null;
  isActive: boolean;
  orderIndex: number;
  afterAction: QuestionnaireAfterAction;
  afterExtension: string | null;
  createdAt: string;
  updatedAt: string;
  activeQuestionsCount?: number;
  sheet?: SheetStatus;
}

export type MenuItemType = 'questionnaire' | 'extension' | 'hangup' | 'main';

export interface MenuItem {
  type: MenuItemType;
  questionnaireId?: string;
  extension?: string;
  label?: string;
}

export interface EntryMenu {
  intro: string;
  items: MenuItem[];
}

export type InputType = 'tap' | 'stt' | 'announcement';

export interface Question {
  id: string;
  questionnaireId: string;
  orderIndex: number;
  key: string;
  label: string;
  promptText: string;
  inputType: InputType;
  options: Record<string, unknown>;
  isRequired: boolean;
  confirmRequired: boolean;
  confirmPrompt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Cycle {
  id: string;
  orgId: string;
  name: string | null;
  startsAt: string;
  endsAt: string | null;
  status: 'open' | 'closed';
  createdBy: string;
  createdAt: string;
  closedAt: string | null;
  closedBy: string | null;
  sessionsCount?: number;
}

export interface RecordItem {
  id: string;
  questionnaireId: string;
  questionnaireName: string | null;
  customerId: string;
  customerName: string | null;
  customerPhone: string | null;
  cycleId: string | null;
  cycleName: string | null;
  cycleStartsAt: string | null;
  startedAt: string;
  endedAt: string | null;
  status: string;
  callerPhone: string | null;
  answers: Record<string, { value_text: string | null; value_num: number | null }>;
}

export type Cadence =
  | 'manual'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'hebrew_monthly'
  | 'unlimited';

export interface Settings {
  cadence: Cadence;
  allow_update_within_cycle: boolean;
  week_start: 'sunday' | 'monday';
  auto_greeting_by_time: boolean;
  greeting_morning: string;
  greeting_afternoon: string;
  greeting_evening: string;
  greeting_night: string;
  greet_with_name_template: string;
  greet_without_name_template: string;
  choose_questionnaire_intro: string;
  closing_message: string;
  already_collected_message: string;
  no_open_cycle_message: string;
  no_questionnaires_message: string;
  no_questions_message: string;
  dashboard_sum_question: { questionnaireId: string; questionKey: string } | null;
  after_action: AfterAction;
  after_extension: string;
  entry_menu: EntryMenu | null;
  ivrTokenMasked: string;
  webhookUrl: string;
}

export type DashboardGroupBy = 'month' | 'cycle';

export interface DashboardData {
  customerCount: number;
  activeQuestionnairesCount: number;
  groupBy: DashboardGroupBy;
  currentLabel: string;
  currentSessions: number;
  currentSum: number | null;
  avgSumPerRecord: number | null;
  chart: { label: string; sessions: number; sum?: number }[];
  statusBreakdown: {
    completed: number;
    abandoned: number;
    inProgress: number;
    total: number;
    completionRate: number;
  };
  perQuestionnaire: { id: string; name: string; sessions: number }[];
  recentSessions: {
    id: string;
    startedAt: string;
    status: string;
    questionnaireName: string | null;
    customerName: string | null;
    customerPhone: string | null;
    callerPhone: string | null;
  }[];
}
